import { RebaseTranslations } from "@rebasepro/cms-types";

/**
 * English baseline translations for @rebasepro/app.
 *
 * This is the single source of truth for all default UI strings.
 * Override any key via the `translations` prop on Rebase entry-point components.
 *
 * @group Core
 */
export const en: RebaseTranslations = {
    // ─── Form actions ────────────────────────────────────────────
    save: "Save",
    create: "Create",
    create_copy: "Create copy",
    save_and_close: "Save and close",
    create_copy_and_close: "Create copy and close",
    create_and_close: "Create and close",
    discard: "Discard",
    clear: "Clear",
    cancel: "Cancel",

    // ─── Entity actions ──────────────────────────────────────────
    edit: "Edit",
    copy: "Copy",
    delete: "Delete",
    delete_not_allowed: "You have selected at least one entity you cannot delete",
    edit_entity: "Edit",
    back_to_detail: "Back to details",

    // ─── Delete dialog ───────────────────────────────────────────
    delete_confirmation_title: "Delete?",
    delete_confirmation_body: "This will delete the entity. Are you sure?",
    delete_multiple_confirmation_body: "This will delete the selected entities. Are you sure?",

    // ─── Unsaved-changes dialog ───────────────────────────────────
    unsaved_changes_title: "Unsaved changes",
    unsaved_changes_body: "You have unsaved changes. Do you want to discard them?",
    discard_changes: "Discard changes",
    keep_editing: "Keep editing",
    clear_form: "Clear form?",
    discard_changes_confirmation: "All unsaved changes will be lost. This cannot be undone.",
    clear_form_confirmation: "All entered values will be cleared. This cannot be undone.",

    // ─── Collection table / toolbar ──────────────────────────────
    search: "Search",
    find_by_id: "Find by ID",
    find_entity_by_id: "Find entity by ID",
    filter: "Filter",
    filters: "Filters",
    records_in_view: "Records in this view",
    clear_filter: "Clear filter",
    clear_filter_sort: "Clear filter/sort",
    clear_sort: "Clear sort",
    sort: "Sort",
    sort_by: "Sort by",
    sort_then_by: "Then by",
    sort_ascending: "Sort ascending",
    sort_descending: "Sort descending",
    sort_remove: "Remove sort",
    sort_move_up: "Make this key more important",
    sort_move_down: "Make this key less important",
    sort_remove_key: "Remove this sort key",
    sort_key_position: "Sort key {{position}}",
    sort_shift_click_hint: "Shift-click to add a column under the current sort",
    save_entity_before_subcollections: "You need to save your entity before adding additional collections",
    clear_all: "Clear all",
    no_items: "No items",
    no_entries_found: "No entries found",
    all_entries_loaded: "All {{count}} entries loaded",
    create_your_first_entry: "Create your first entry",
    no_results_filter_sort: "No results with the applied filter/sort",
    no_results_search: "No results found for \"{{search}}\"",
    add: "Add",
    remove: "Remove",
    multiple_entities: "Multiple entities",
    unsaved_changes: "You have unsaved changes in this {{collectionName}}.",
    so_empty: "So empty...",
    no_results: "No results with the applied filter/sort",
    refresh_data: "Refresh data",
    dark_mode: "Dark",
    light_mode: "Light",
    system_mode: "System",
    join_newsletter: "Join the newsletter, no spam",
    ok: "Ok",
    save_collection_config: "Save collection structure",
    search_for_more_icons: "Search for more icons…",
    ai_modified: "AI modified",
    size_label: "Size",
    group_by: "Group by",
    initialize_kanban_order: "Initialize Kanban Order",
    copy_id: "Copy ID",
    add_specific: "Add {{name}}",
    add_named: "Add \"{{name}}\"",
    select_specific: "Select {{name}}",
    select_from: "Select from {{name}}",
    done: "Done",
    log_out: "Log out",
    column_cannot_be_edited: "This column can't be edited directly",
    close: "Close",
    hide_list: "Hide list",
    show_list: "Show list",
    unsaved_local_changes: "Unsaved Local changes",
    unsaved_local_changes_description: "This document was edited locally and has unsaved changes. These local changes will be lost if you don't apply them.",
    preview_changes: "Preview Changes",
    apply_changes: "Apply Changes",
    discard_local_changes: "Discard Local Changes",
    preview_local_changes: "Preview Local Changes",
    preview_local_changes_description: "These are the local changes that will be applied to the form.",
    type: "Type",
    string: "string",
    number: "number",
    boolean: "boolean",
    date: "date",
    map: "map",
    array: "array",
    arrays_of_arrays_not_supported: "Arrays of arrays are not supported.",
    data_type_not_supported: "Data type {{dataType}} not supported yet",
    passkey_error_unsupported: "Your device or browser doesn't support passkeys.",

    // ─── Entity form feedback ─────────────────────────────────────
    saved_correctly: "Saved successfully",
    error_before_saving: "Error preparing data",
    error_after_saving: "Error in post-save hook",
    error_saving_entity: "Error saving entity",
    entity_does_not_exist: "This entity does not exist in the database",
    form_modified: "This form has been modified",
    form_in_sync: "The current form is in sync with the database",
    fix_errors_before_saving: "Fix highlighted errors before saving",

    admin: "Admin",
    home: "Home",
    this_form_has_errors: "This form has errors",
    error_loading_navigation: "Error loading navigation",
    error_loading_auth: "Error loading auth",
    this_form_has_been_modified: "This form has been modified",
    current_form_in_sync: "The current form is in sync with the database",
    open_in_console: "Open in the console",
    collection_does_not_exist: "The specified collection does not exist. Check console",
    unexpected_value: "Unexpected value",
    unexpected_value_click_to_edit: "Unexpected value. Click to edit",
    unexpected_reference_value: "Unexpected reference value. Click to edit",
    copy_url_to_clipboard: "Copy url to clipboard",
    open_image_in_new_tab: "Open image in new tab",
    reference_not_set: "Reference not set",
    reference_does_not_exist: "Reference does not exist",
    entity_not_found: "Entity not found",
    entity_not_found_body: "This record does not exist, or it may be hidden from you by row-level security.",
    back_to_collection: "Back to {{name}}",
    file_not_found: "File not found",
    unsaved_changes_in_entity: "You have unsaved changes in this entity.",
    delete_this_role: "Delete this role",
    no_roles_yet: "You don't have any roles yet.",
    create_default_roles: "Create default roles",
    delete_role_confirmation: "Are you sure you want to delete this role?",
    delete_this_user: "Delete this user",
    no_users_yet: "There are no users yet",
    add_logged_user_as_admin: "Add the logged user as an admin",
    add_current_user_as_admin: "Add current user as admin",
    create_default_roles_and_add_admin: "Create default roles and add current user as admin",
    delete_user_confirmation: "Are you sure you want to delete this user?",
    create_your_users_and_roles: "Create your users and roles",
    no_users_or_roles_defined: "You have no users or roles defined. You can create default roles and add the current user as admin.",
    save_before_changing_schema: "You need to save the document before changing the schema",
    edit_schema_for_this_form: "Edit schema for this form",
    no_permissions_to_edit_collection: "You don't have permissions to edit this collection",
    browser_does_not_support_audio: "Your browser does not support the audio element.",
    user_not_found: "User not found: {{value}}",

    // ─── Collection view actions ──────────────────────────────────
    delete_selected: "Delete",
    cannot_delete_selected: "You have selected at least one entity you cannot delete",

    // ─── Array / field containers ─────────────────────────────────
    add_entry: "Add",
    add_on_top: "Add on top",
    add_below: "Add below",
    add_to_field: "Add to {{fieldName}}",
    value: "Value",
    key: "Key",
    drag_drop_multiple: "Drag 'n' drop some files here, or click to select files. Drag to reorder.",
    drag_drop_single: "Drag 'n' drop a file here, or click to select one",

    // ─── Navigation / scaffold ────────────────────────────────────
    open_menu: "Open menu",
    close_drawer: "Close drawer",
    navigation_drawer: "Navigation drawer",
    collapse: "Collapse",
    expand: "Expand",
    change_language: "Change language",
    toggle_theme: "Toggle theme",
    user_menu: "User menu",

    // ─── Error states ─────────────────────────────────────────────
    error: "Error",
    error_loading_data: "Could not load data",
    error_check_server_logs: "Check server logs for details.",
    error_technical_details: "Technical details",
    error_uploading_file: "Error uploading file",
    error_deleting: "Error deleting",
    error_before_delete: "Error before delete",
    error_updating_asset: "Error updating asset",
    error_deleting_asset: "Error deleting asset",
    error_firestore_index: "A Firestore index is required for this query.",
    create_index: "Create Index",
    value_is_not_reference: "Value is not a reference.",
    click_to_edit: "Click to edit",
    data_is_not_array_of_references: "Data is not an array of references",

    // ─── Misc ─────────────────────────────────────────────────────
    loading: "Loading",
    local_changes_applied: "Local changes applied to the form",
    local_changes_discarded: "Local changes discarded",
    are_you_sure_leave: "Are you sure you want to leave this page?",
    see_console_details: "See console for more details.",
    drop_here_create_group: "Drop here to create a new group",
    filter_for_null_values: "Filter for null values",
    value_updated_elsewhere: "This value has been updated elsewhere",
    add_property: "Add property",
    edit_name: "Edit {{name}}",
    this_entity_not_exist: "This entity does not exist in the database",
    internal_error: "Internal ERROR",

    // ─── Rename group dialog ──────────────────────────────────────
    rename_group: "Rename Group",
    group_name_label: "Group Name",
    group_name_empty_error: "Group name cannot be empty.",
    group_name_exists_error: "This group name already exists.",

    // ─── Search ───────────────────────────────────────────────────
    search_collections: "Search collections",

    // ─── Navigation groups ────────────────────────────────────────
    views_group: "Views",

    // ─── Entity Edit View ─────────────────────────────────────────
    youd_need_to_save_before_additional_collections: "You need to save your entity before adding additional collections",

    // ─── Not Found Page ───────────────────────────────────────────
    page_not_found: "Page not found",
    page_not_found_body: "This page does not exist or you may not have access to it",
    back_to_home: "Back to home",

    // ─── Collection Editor ─────────────────────────────────────────
    default_collection_view: "Default collection view",
    table_view: "Table",
    cards_view: "Cards",
    kanban_view: "Kanban",
    choose_how_entities_displayed_default: "Choose how entities should be displayed by default",

    document_view: "Document view",
    side_panel: "Side panel",
    full_screen: "Full screen",
    should_documents_opened_full_screen: "Should documents be opened full screen or in an inline side dialog",

    select_custom_view: "Select custom view",
    no_custom_views_defined: "No custom views defined. Define your custom views in the customization settings, before using this dialog.",

    select_custom_action: "Select custom action",
    no_custom_actions_defined: "No custom actions defined. Define your custom actions in the customization settings, before using this dialog.",

    no_collections_found: "No collections found",
    start_building_collections: "Start building collections in Rebase easily. Map them to your existing database data, import from files, or use our templates.",
    create_first_collection: "Create your first collection",
    define_collections_programmatically: "You can also define collections programmatically.",
    edit_collection: "Edit collection",
    no_permissions_edit_collection: "You don't have permissions to edit this collection",
    no_permissions_create_collection: "You don't have permissions to create collections",
    create_collection: "Create collection",
    update_collection: "Update collection",

    new_collection: "New collection",
    add_new_collection: "Add new collection",
    collection_with_name: "{{name}} collection",
    change_icon: "Change icon",
    is_subcollection_of: "This is a subcollection of",
    name: "Name",
    collection_name_description: "Name of this collection, usually a plural name (e.g. Products)",
    path: "Path",
    relative_path_to_parent: "Relative path to the parent (no need to include the parent path)",
    path_in_database: "Path or table name where this collection is stored in the database",
    singular_name: "Singular name",
    singular_name_description: "Optionally define a singular name for your entities",
    description: "Description",
    description_of_collection: "Description of the collection, you can use markdown",
    collection_id: "Collection ID",
    collection_id_description: "This ID identifies this collection. Typically the same as the path or table name.",
    collection_group: "Collection group",
    collection_group_description: "A collection group consists of all collections with the same path. This allows you to query over multiple collections at once.",
    advanced_settings: "Advanced settings",
    doc_history_global: "Document history revisions enabled if enabled globally",
    doc_history_enabled: "Document history revisions ENABLED",
    doc_history_not_enabled: "Document history revisions NOT enabled",
    doc_history_description: "When enabled, each document in this collection will have a history of changes. This is useful for auditing purposes. The data is stored in a subcollection of the document in your database, called __history.",
    document_id_generation: "Document IDs generation",
    code_defined: "Code defined",
    users_must_define_id: "Users must define an ID",
    users_can_define_id: "Users can define an ID, but it is not required",
    doc_id_auto_generated: "Document ID is generated automatically",
    config_doc_id_generation: "Configure how document IDs are generated when creating new entities.",
    enable_text_search: "Enable text search for this collection",
    text_search_description: "Allow text search for this collection. If you have not specified a text search delegate, this will use the built-in local text search. This is not recommended for large collections, as it may incur in performance and cost issues.",
    database_id: "Database ID",
    default_text: "(default)",

    custom_actions: "Custom actions",
    more_info: "More info",
    define_custom_actions_cli: "Define your own custom actions by uploading them with the CLI.",
    action_defined_in_code: "This action is defined in code with key",
    add_custom_entity_action: "Add custom entity action",
    remove_this_action: "Remove this action?",
    remove_action_warning: "This will not delete any data, only the action in the admin panel",

    subcollections_of: "Subcollections of",
    add_subcollection: "Add subcollection",
    custom_views: "Custom views",
    define_custom_views_cli: "Define your own custom views by uploading them with the CLI.",
    view_defined_in_code: "This view is defined in code with key",
    add_custom_entity_view: "Add custom entity view",
    delete_this_subcollection: "Delete this subcollection?",
    remove_collection_warning: "This will not delete any data, only the collection in the admin panel",
    remove_this_view: "Remove this view?",
    remove_view_warning: "This will not delete any data, only the view in the admin panel",

    no_collection_selected: "No collection selected",
    code_for_collection: "Code for",
    use_config_define_json: "Use this config to define the collection in JSON format.",
    customise_collection_code: "If you want to customise the collection in code, you can add this collection code to your admin app configuration.",
    copied: "Copied",

    property_cant_be_edited: "This property can't be edited",
    property_not_editable_description: "You may not have permission to edit it or it is defined in code and cannot be modified.",
    delete_this_property: "Delete this property?",
    delete_property_warning: "This will not delete any data, only modify the collection.",
    error_must_specify_id: "You must specify an id for the field",
    error_id_format: "The id can only contain letters, numbers and underscores (_), and not start with a number",
    error_id_already_exists: "There is another field with this ID already",
    error_must_specify_title: "You must specify a title for the field",
    custom_or_other: "Custom/Other",
    select_property_widget: "Select a property widget",
    error_changing_data_type: "This widget uses a different data type than the initially selected widget. This can cause errors with existing data.",
    required: "Required",

    enum_form_dialog: "Enum form dialog",
    imported_data_preview: "Imported data preview",
    entities_with_same_id_overwritten: "Entities with the same id will be overwritten",
    collection_editor: "Collection editor",
    properties_in_this_group: "Properties in this group",
    data_property_mapping: "Data property mapping",
    property_edit_view: "Property edit view",
    all_of_these: "All of these",
    any_of_these: "Any of these",

    only_admins_edit_roles: "Only admins can edit roles",
    error_user_not_found: "User not found",
    all_roles: "All Roles",
    role: "Role",
    name_of_this_role: "Name of this role",
    id_of_this_role: "ID of this role",
    create_entities: "Create entities",
    read_entities: "Read entities",
    update_entities: "Update entities",
    delete_entities: "Delete entities",
    all_collections: "All collections",
    create_entities_in_collections: "Create entities in collections",
    access_all_data_in_every_collection: "Access all data in every collection",
    update_data_in_any_collection: "Update data in any collection",
    delete_data_in_any_collection: "Delete data in any collection",
    allow_all_permissions_in_this_collections: "Allow all permissions in this collections",
    all: "All",
    customise_permissions_description: "You can customise the permissions that the users related to this role can perform in the entities of each collection",
    create_collections: "Create collections",
    yes: "Yes",
    no: "No",
    can_user_create_collections: "Can the user create collections",
    edit_collections: "Edit collections",
    only_own_collections: "Only his/her own",
    own: "Own",
    can_user_edit_collections: "Can the user edit collections",
    delete_collections: "Delete collections",
    can_user_delete_collections: "Can the user delete collections",
    error_saving_role: "There was an error saving this role",
    create_role: "Create role",
    update: "Update",

    only_admins_change_roles: "Only admins can change roles",
    must_be_at_least_one_admin: "There must be at least one admin",
    logged_user_not_found: "Logged user not found",
    user: "User",
    user_id: "User ID",
    name_of_this_user: "Name of this user",
    email_of_this_user: "Email of this user",
    roles: "Roles",
    create_user: "Create user",

    users: "Users",
    add_user: "Add user",
    add_role: "Add role",
    is_admin: "Is Admin",
    default_permissions: "Default permissions",
    created_on: "Created on",
    email: "Email",
    id: "ID",
    read: "Read",

    column_in_file: "Column in file",
    map_to_property: "Map to Property",
    default_values: "Default values",
    default_values_description: "You can select a default value for unmapped columns and empty values:",
    property: "Property",
    default_value: "Default value",
    autogenerate_id: "Autogenerate ID",
    id_column_description: "Column that will be used as ID for each document",
    do_not_set_value: "Do not set value",
    set_value_to_true: "Set value to true",
    set_value_to_false: "Set value to false",
    drag_and_drop_file: "Drag and drop a file here or click to upload",
    error_saving_data: "Error saving data",
    retry: "Retry",
    saving_data: "Saving data",
    entities_saved: "entities saved",
    do_not_close_tab: "Do not close this tab or the import will be interrupted",
    import: "Import",
    import_data: "Import data",
    upload_file_description: "Upload a CSV, Excel or JSON file and map it to your existing schema",
    back: "Back",
    next: "Next",
    save_data: "Save data",
    use_column_as_id: "Use this column as ID",
    do_not_import_property: "Do not import this property",
    entities_will_be_overwritten: "Entities with the same id will be overwritten",
    data_imported_successfully: "Data imported successfully",
    export: "Export",
    export_data: "Export data",
    download_table_csv: "Download the content of this table as a CSV",
    csv: "CSV",
    json: "JSON",
    dates_as_timestamps: "Dates as timestamps",
    dates_as_strings: "Dates as strings",
    flatten_arrays: "Flatten arrays",
    download: "Download",
    large_number_of_documents: "This collection has a large number of documents ({{count}}).",
    too_many_documents_to_export: "This collection has more documents ({{count}}) than the browser can export in a single file (limit {{limit}}). Filter it down before exporting.",
    include_undefined_values: "Include undefined values",
    submit: "Submit",

    no_filterable_properties: "No filterable properties available",
    apply_filters: "Apply filters",

    // ─── Filter Presets ──────────────────────────────────────────
    filter_presets: "Presets",
    filter_preset_apply: "Apply preset",
    filter_preset_active: "Active: {{label}}",

    list: "List",
    table_view_mode: "Table",
    cards: "Cards",
    board: "Board",
    initialize_kanban_order_desc: "This will assign sequential order values to all items that don't have one. Items will maintain their current order within each column.",
    kanban_view_not_available: "Kanban view is not available",
    kanban_view_requires_enum: "Kanban view requires a string property with enum values to group entities into columns. Please add an enum property to your collection schema to use this view.",
    no_enum_values_configured: 'No enum values configured for property "{{property}}"',
    items_need_backfill: "Some items don't have order values. Initialize to enable drag-and-drop reordering.",
    kanban_order_not_configured: "Drag-and-drop ordering is not configured. Set an order property in the collection settings to persist card positions.",
    initialize: "Initialize",
    confirm_multiple_delete: "Confirm multiple delete?",
    delete_entity_confirm_title: "Would you like to delete this {{entityName}}?",
    add_existing: "Add existing",
    unlink_entity_confirm_title: "Remove this {{entityName}} from this record?",
    unlink_entity_confirm_body: "It stays in {{collectionName}} and remains available to other records.",
    confirm_multiple_unlink: "remove these from this record?",
    no_filter: "No filter",
    is_true: "is true",
    is_false: "is false",
    error_uploading_file_size: "Error uploading file: File is larger than {{maxSize}} bytes",
    error_uploading_file_type: "Error uploading file: File type is not supported",
    drag_drop_files: "Drag 'n' drop some files here, or click here to edit",
    drag_drop_file: "Drag 'n' drop a file here, or click here to edit",
    multiple_deleted: "Multiple deleted",
    some_entities_deleted: "Some of the entities have been deleted, but not all",
    error_deleting_entities: "Error deleting entities",
    deleted: "Deleted",
    selection_options: "Selection options",
    selection_menu_all_loaded: "All on this page",
    selection_menu_all_matching: "All {{total}} {{collection}}",
    selection_menu_none: "None",
    selection_select_all_loaded: "Select all loaded rows",
    selection_deselect_all: "Deselect all",
    selection_reading_rows: "Reading rows… {{loaded}} of {{total}}",
    selection_reading_rows_unknown: "Reading rows… {{loaded}} so far",
    selection_deleting_progress: "Deleted {{completed}} of {{total}}",
    confirm_delete_selection: "Delete {{total}} {{collection}}?",
    confirm_delete_selection_unknown: "Delete every row matching the current filter?",
    confirm_delete_selection_body: "This cannot be undone. Each row is deleted individually, so this may take a while.",

    export_selection_count: "Download the {{total}} selected rows as a {{format}}",
    export_selection_all_matching: "Download every {{collection}} matching the current filter",

    select_reference: "Select reference",
    select_references: "Select references",
    account_settings: "Account Settings",
    profile: "Profile",
    security: "Security",
    sessions: "Sessions",
    change_password: "Change Password",
    current_password: "Current Password",
    new_password: "New Password",
    confirm_password: "Confirm New Password",
    password_changed: "Password changed successfully. You will be logged out.",
    passwords_dont_match: "Passwords don't match",
    password_too_short: "Password must be at least 8 characters",
    password_change_not_available: "Password change is not available for accounts using external sign-in providers.",
    changing_password: "Changing...",
    display_name: "Display Name",
    photo_url: "Photo URL",
    save_profile: "Save Profile",
    saving: "Saving...",
    no_active_sessions: "No active sessions found.",
    revoking: "Revoking...",
    revoke_all_sessions: "Revoke All Sessions",
    unknown_device: "Unknown Device",
    current: "Current",
    role_id: "Role ID",
    role_name: "Role Name",
    add_reference: "Add reference",
    add_reference_to: "Add reference to {{name}}",

    /** RolesView — table & permissions */
    role_deleted_successfully: "Role deleted successfully",
    error_deleting_role: "Error deleting role",
    collection_permissions: "Collection permissions",
    collection: "Collection",
    no_collections_configured: "No collections configured",
    no_security_rules_defined: "No security rules defined — all operations unrestricted",
    no_rules: "no rules",

    /** UsersView — snackbar */
    bootstrap_admin_success: "You are now an admin! Refreshing...",
    failed_to_bootstrap_admin: "Failed to bootstrap admin",
    user_deleted_successfully: "User deleted successfully",
    error_deleting_user: "Error deleting user",
    user_created_successfully: "User created successfully",
    invitation_sent: "An invitation email has been sent to {{email}}. They can use the link to set their password.",
    invitation_sent_title: "Invitation Sent",
    temporary_password: "Temporary Password",
    temporary_password_description: "Email is not configured. Share this temporary password with the user securely. It will not be shown again.",
    temporary_password_email_failed_description: "The email could not be delivered. Share this temporary password with the user securely. It will not be shown again.",
    copy_password: "Copy password",
    password_copied: "Password copied to clipboard",

    /** UsersView — pagination & search */
    search_users: "Search users",
    created: "Created",
    actions: "Actions",
    no_users_found: "No users found",
    reset_password: "Reset Password",
    reset_password_success: "Password reset successfully",
    reset_password_confirmation: "Are you sure you want to reset this user's password?",
    reset_password_send_email: "Send a password reset email",
    reset_password_set_manually: "Set a password manually",
    reset_password_set_manually_description: "The password is updated immediately and no email is sent. Share it with the user securely.",

    /** Permission-denied empty states */
    no_permission_to_view_users: "You don't have permission to view users",
    no_permission_to_view_roles: "You don't have permission to view roles",
    no_permission_description: "Contact an administrator if you need access to this section.",
    error_resetting_password: "Error resetting password",

    /** Editor table-bubble */
    add_row_before: "Add row before",
    add_row_after: "Add row after",
    delete_row: "Delete row",
    add_column_before: "Add column before",
    add_column_after: "Add column after",
    delete_column: "Delete column",
    delete_table: "Delete table",

    /** Editor image-bubble */
    alt_text: "Alt text",
    title: "Title",

    /** MapFieldBinding */
    a_property_of_this_map_has_error: "A property of this map has an error",

    /** AI Collection Generator Popover */
    generate_collection_with_ai: "Generate Collection with AI",
    modify_collection_with_ai: "Modify Collection with AI",
    describe_collection_to_create: "Describe the collection you want to create.",
    describe_changes_to_make: "Describe the changes you want to make to this collection.",
    ai_placeholder_create: "e.g., Create a products collection with name, price, description, and category...",
    ai_placeholder_modify: "e.g., Add a thumbnail image field with storage, make price required...",
    ai_assist: "AI Assist",
    generating: "Generating...",

    /** Recently extracted strings for collection editor */
    this_is_subcollection_of: "This is a subcollection of",
    use_existing_paths_database: "Use one of the existing paths in your database:",
    describe_collection_ai: "Describe your collection to AI:",
    generate_with_ai: "Generate with AI",
    create_from_json_config: "Create from JSON configuration:",
    paste_json_config: "Paste JSON Configuration",
    create_collection_from_file_formats: "Create a collection from a file (csv, json, xls, xslx...)",
    select_template: "Select a template:",
    products: "Products",
    collection_products_subtitle: "A collection of products with images, prices and stock",
    collection_users_subtitle: "A collection of users with emails, names and roles",
    blog_posts: "Blog posts",
    collection_blog_posts_subtitle: "A collection of blog posts with images, authors and complex content",
    pages: "Pages",
    collection_pages_subtitle: "A collection of pages with images, authors and complex content",
    continue_from_scratch: "Continue from scratch",

    /** Admin views config */
    admin_users: "Admin Users",
    roles_menu: "Roles",
    project_settings: "Project settings",

    by_signing_in_you_agree_to_our: "By signing in you agree to our",
    terms_and_conditions: "Terms and Conditions",
    and_our: "and our",
    privacy_policy: "Privacy policy",
    email_password: "Email/password",
    sign_in_with_google: "Sign in with Google",

    // --- Auth error messages ---
    auth_user_not_found: "User not found",
    auth_wrong_password: "Wrong password. Please try again.",
    auth_user_disabled: "User disabled. Please contact support.",
    auth_account_exists_with_different_credential: "Account exists with different sign in method",
    auth_email_already_in_use: "The email is already in use",
    auth_google_permissions_required: "You need to grant additional permissions in order to manage your Google Cloud projects",
    auth_invalid_email_password: "Please enter both email and password",
    auth_enter_email_first: "Please enter your email first",
    auth_password_reset_sent: "Password reset email sent",
    auth_sign_in_account: "Sign in to your account",
    auth_create_new_account: "Create a new account",
    auth_password: "Password",
    auth_reset_password: "Reset password",
    auth_new_user: "New user?",
    auth_have_account: "Have an account?",
    auth_sign_in: "Sign In",
    auth_sign_up: "Sign Up",


    auto_setup_collections_button: "Automatically set up collections",
    auto_setup_collections_title: "Automatically set up collections?",
    auto_setup_collections_desc: "This will automatically create collection configs for collections that are <b>NOT</b> already mapped",
    this_can_take_a_minute: "This can take a minute or two",
    no_collections_found_to_setup: "No collections found to setup.",
    collections_have_been_setup: "Collections have been automatically setup.",
    error_setting_up_collections: "Error automatically setting up collections",

    // --- Home Suggestions ---

    admin_panel_ready_bring_data: "Your admin panel is ready. Let's bring in your data.",
    admin_panel_ready_get_started: "Your admin panel is ready. Here's how to get started.",
    auto_detect_collections: "Auto-detect collections",
    auto_detect_collections_desc: "Let AI scan your database and automatically generate collection schemas.",
    create_a_collection: "Create a collection",
    create_collection_desc: "Manually define your first collection from scratch using the visual editor.",
    read_the_docs: "Read the docs",
    read_the_docs_desc: "Learn how to customize fields, views, actions and more.",
    explore_docs: "Explore docs",
    want_to_customize_with_code: "Want to customize with code? Run",
    to_scaffold_a_local_project: "to scaffold a local project.",

    // ─── Collection Editor — Validation ──────────────────────────
    validation: "Validation",
    unique: "Unique",
    required_message: "Required message",
    required_tooltip: "You won't be able to save this entity if this value is not set",
    unique_tooltip: "There cannot be multiple entities with the same value",
    lowercase: "Lowercase",
    uppercase: "Uppercase",
    trim: "Trim",
    exact_length: "Exact length",
    min_length: "Min length",
    max_length: "Max length",
    matches_regex: "Matches regex",
    not_valid_regexp: "Not a valid regexp",
    regex_helper: "e.g. /^\\d+$/ for digits only",
    min_value: "Min value",
    max_value: "Max value",
    less_than: "Less than",
    more_than: "More than",
    positive_value: "Positive value",
    negative_value: "Negative value",
    integer_value: "Integer value",

    // ─── Collection Editor — Property Edit ───────────────────────
    invalid_regular_expression: "Invalid regular expression",
    must_specify_target_collection: "You must specify a target collection for the field",
    need_specify_repeat_field: "You need to specify a repeat field",
    need_specify_block_properties: "You need to specify the properties of this block",
    incomplete_condition: "Incomplete condition - please select a field",
    field_name: "Field name",

    // ─── Collection Editor — Display & Config ────────────────────
    kanban_column_property: "Kanban Column Property",
    select_a_property: "Select a property",
    kanban_property_not_found: "Property \"{{property}}\" does not exist or is not an enum string property. Please select a valid property or clear the selection.",
    no_enum_string_properties: "No enum string properties found. Add a string property with enumValues to use Kanban view.",
    kanban_column_description: "Select a string property with enum values to group entities into columns",
    create_property: "+ Create \"{{property}}\" property",
    order_property: "Order Property",
    order_property_not_found: "Property \"{{property}}\" does not exist or is not a text property. Please select a valid property or clear the selection.",
    no_number_properties: "No text properties found. Add a text property to enable ordering.",
    order_property_description: "Select a text property to persist the order of items",
    display_settings: "Display settings",
    default_row_size: "Default row size",
    side_dialog_width: "Side dialog width",
    side_dialog_width_description: "Optionally define the width (in pixels) of entities side dialog. Default is 768px",
    inline_editing_enabled: "Data can be edited directly in the table view",
    inline_editing_disabled: "Data can be edited only in the form view",
    inline_editing_description: "Allow editing data directly in the table view, without opening the form view.",
    include_json_view: "Include JSON view",
    no_json_view: "Do not include JSON view",
    json_view_description: "Include the JSON representation of the document.",
    not_found_suffix: "not found",

    // ─── Editor ─────────────────────────────────────────────────
    editor_text: "Text",
    editor_text_description: "Just start typing with plain text.",
    editor_heading_1: "Heading 1",
    editor_heading_1_description: "Big section heading.",
    editor_heading_2: "Heading 2",
    editor_heading_2_description: "Medium section heading.",
    editor_heading_3: "Heading 3",
    editor_heading_3_description: "Small section heading.",
    editor_todo_list: "To-do List",
    editor_todo_list_description: "Track tasks with a to-do list.",
    editor_bullet_list: "Bullet List",
    editor_bullet_list_description: "Create a simple bulleted list.",
    editor_numbered_list: "Numbered List",
    editor_numbered_list_description: "Create a list with numbering.",
    editor_quote: "Quote",
    editor_quote_description: "Capture a quote.",
    editor_code: "Code",
    editor_code_description: "Capture a code snippet.",
    editor_image: "Image",
    editor_image_description: "Upload an image from your computer.",
    editor_multiple: "Multiple",
    editor_link: "Link",
    editor_save: "Save",
    editor_cancel: "Cancel",
    editor_remove_link: "Remove link",
    editor_paste_or_type_link: "Paste or type link",
    editor_open_in_new_window: "Open in new window",
    editor_bold: "Bold",
    editor_italic: "Italic",
    editor_underline: "Underline",
    editor_strikethrough: "Strikethrough",
    editor_autocomplete: "Autocomplete",
    editor_autocomplete_description: "Add text based on the context.",

    // ─── Text Search Dialog ─────────────────────────────────────
    text_search_dialog_title: "Enable text search",
    text_search_local_not_recommended: "Local text search is not recommended for large collections.",
    text_search_local_fetch_warning: "Note that enabling local text search will need to fetch all documents from your collection and store them in the browser. This can be inefficient for large collections. It can also incur in additional costs.",
    text_search_external_suggestion: "For larger collections, you are encouraged to use an external search engine such as Algolia or Elastic Search, and assign a search delegate to your config.",
    text_search_local_description: "Local text search is the simplest way to enable text search in your collection. It loads all documents in the collection in the browser and performs the search locally. This is the recommended option for small collections.",
    text_search_own_implementation: "You have implemented your own text search controller. You enable text search for your collection.",
    text_search_enable_for_collection: "Enable for this collection",
    text_search_enable_for_project: "Enable for project",
    text_search_enabled_snackbar: "Local text search enabled",

    settings_heading: "Settings",
    settings_project_name: "Project name",
    settings_default_language: "Default language",
    settings_default_language_caption: "Select the base language for this project. Users can override this preference in their personal settings.",
    settings_enable_local_text_search: "Enable local text search",
    settings_local_text_search_caption: "Enable local text search for all collections. This will allow you to search text fields in your collections using the Rebase search bar. Note that this feature can incur in higher read counts, as it will index all text fields in your collections.",
    settings_doc_history_all_collections: "Document history enabled for all collections",
    settings_doc_history_caption: "When true, all collections will have the history enabled by default. You can override this setting in each collection. History will be saved in the __history subcollection of each document.",
    settings_theme: "Theme",
    settings_primary_color: "Primary color",
    settings_secondary_color: "Secondary color",
    settings_sample_theme_components: "Sample theme components",
    settings_drag_drop_logo: "Drag and drop your logo here",
    settings_security_rules: "Security rules",
    settings_security_rules_description: "Rebase uses granular security rules to restrict access to data. When creating a new user through Rebase, custom claims are added to the user. By adding the following security rules to your project, you ensure Rebase users can access data securely.",
    settings_security_rules_add_domain: "Remember to add the domain {{domain}} to your provider allowed domains",
    settings_security_rules_caption: "These rules restrict access to data to Rebase users only, but do not enforce the permissions at the database level. The permissions are enforced in the frontend though, which will work fine for most projects. If you need to enforce the permissions at the database level, you can modify these security rules yourself to suit your needs. The roles assigned to a user are set as custom claims in the auth token, so you can use them in your security rules.",

    // ─── Studio: SQL Editor ─────────────────────────────────────
    studio_sql_executing_query: "EXECUTING QUERY...",
    studio_sql_query_error: "Query Error",
    studio_sql_run_query_placeholder: "Run a query to see results",
    studio_sql_visual_execution_plan: "Visual Execution Plan",
    studio_sql_success: "SUCCESS",
    studio_sql_no_results: "Query executed successfully but returned no results.",
    studio_sql_rows: "Rows:",
    studio_sql_time: "Time:",
    studio_sql_copy_markdown: "Copy Markdown",
    studio_sql_export_json: "Export JSON",
    studio_sql_export_csv: "Export CSV",
    studio_sql_format_sql: "Format SQL",
    studio_sql_explain: "Explain",
    studio_sql_limit_1000: "Limit 1000",
    studio_sql_remove_from_favorites: "Remove from Favorites",
    studio_sql_add_to_favorites: "Add to Favorites",
    studio_sql_save: "Save",
    studio_sql_run: "Run",
    studio_sql_database: "Database",
    studio_sql_role: "Role",
    studio_sql_admin: "(Admin)",
    studio_sql_select_db: "Select DB",
    studio_sql_query_results: "Query Results",
    studio_sql_save_snippet: "Save SQL Snippet",
    studio_sql_snippet_name: "Snippet Name",
    studio_sql_snippet_name_placeholder: "e.g., Get All Users",
    studio_sql_snippet_saved_local: "This will be saved to your local storage.",
    studio_sql_cancel: "Cancel",
    studio_sql_dangerous_operation: "Dangerous Operation Detected",
    studio_sql_dangerous_operation_body: "The query you are about to run contains potentially destructive operations (DELETE, DROP, TRUNCATE, or UPDATE without WHERE). Are you sure you want to proceed?",
    studio_sql_snippet_saved: "Snippet \"{{name}}\" saved.",
    studio_sql_markdown_copied: "Results copied as Markdown!",
    studio_sql_markdown_copy_failed: "Failed to copy Markdown to clipboard.",
    studio_sql_row_updated: "Row updated successfully.",
    studio_sql_cannot_edit_missing_query: "Cannot edit rows: The underlying query is missing.",
    studio_sql_cannot_resolve_table: "Could not resolve table/primary key",
    studio_sql_missing_pk: "Row is missing primary key column(s): {{columns}}. Cannot safely update.",
    studio_sql_update_failed: "Failed to update: {{message}}",
    studio_sql_execution_not_supported: "SQL execution is not supported by the current data source.",
    studio_sql_error_executing: "An error occurred while executing the query.",
    studio_sql_error_explaining: "An error occurred while explaining the query.",
    studio_sql_save_first_to_favorite: "Please save the snippet first before favoriting.",
    studio_sql_collections_label: "Collections:",
    studio_sql_admin_collections_tooltip: "Tables in this query that are mapped to collections",
    studio_sql_edit_entity: "Open {{name}} #{{id}}",
    studio_sql_sql_not_supported: "SQL execution not supported by this data source",
    studio_sql_fetch_error: "Failed to fetch connection options: {{message}}",
    studio_sql_unexpected_format: "Unexpected data format: {{type}}",
    studio_sql_no_tables: "No tables found in this database.",
    studio_sql_schema_fetch_error: "Failed to fetch schema: {{message}}",

    // ─── Studio: SQL Editor Sidebar ──────────────────────────────
    studio_sql_sidebar_snippets: "Snippets",
    studio_sql_sidebar_history: "History",
    studio_sql_sidebar_schema: "Schema",
    studio_sql_sidebar_no_snippets: "No saved snippets",
    studio_sql_sidebar_save_snippet_hint: "Save a query to access it here.",
    studio_sql_sidebar_no_history: "No history",
    studio_sql_sidebar_history_hint: "Executed queries will appear here.",
    studio_sql_sidebar_delete_snippet: "Delete snippet",
    studio_sql_close_tab: "Close this query tab",
    studio_sql_new_tab: "New query tab",

    // ─── Studio: Schema Browser ──────────────────────────────────
    studio_schema_tables: "Tables",
    studio_schema_loading: "Loading schema...",
    studio_schema_no_tables: "No tables found",
    studio_schema_retry: "Retry",
    studio_schema_primary_key: "Primary Key",
    studio_schema_select_all: "SELECT * FROM",
    studio_schema_insert_into: "INSERT INTO",
    studio_schema_update: "UPDATE",
    studio_schema_delete_from: "DELETE FROM",
    studio_schema_columns: "{{count}} columns",

    // ─── Studio: RLS Editor ──────────────────────────────────────
    studio_rls_title: "Row Level Security",
    studio_rls_description: "Manage RLS policies for your tables",
    studio_rls_enabled: "RLS Enabled",
    studio_rls_disabled: "RLS Disabled",
    studio_rls_no_rls: "No RLS",
    studio_rls_enable_rls: "Enable RLS",
    studio_rls_disable_rls: "Disable RLS",
    studio_rls_create_policy: "Create Policy",
    studio_rls_policies: "Policies",
    studio_rls_no_policies: "No policies defined",
    studio_rls_no_policies_desc: "This table has no RLS policies. All rows are accessible based on the RLS status.",
    studio_rls_add_first_policy: "Add your first policy",
    studio_rls_force_rls: "Force RLS",
    studio_rls_force_rls_desc: "When enabled, RLS policies apply to the table owner as well.",
    studio_rls_enable_force_rls: "Enable Force RLS",
    studio_rls_disable_force_rls: "Disable Force RLS",
    studio_rls_edit: "Edit",
    studio_rls_delete: "Delete",
    studio_rls_confirm_delete_title: "Delete Policy",
    studio_rls_confirm_delete_body: "Are you sure you want to delete this policy? This action cannot be undone.",
    studio_rls_confirm_enable_title: "Enable Row Level Security",
    studio_rls_confirm_enable_body: "Enabling RLS will restrict access to rows in this table based on your policies. Make sure you have appropriate policies in place.",
    studio_rls_confirm_disable_title: "Disable Row Level Security",
    studio_rls_confirm_disable_body: "Disabling RLS will make all rows accessible to all roles. Are you sure you want to continue?",
    studio_rls_select_table: "Select a table",
    studio_rls_no_tables: "No tables available",
    studio_rls_error: "Error loading policies",
    studio_rls_retry: "Retry",
    studio_rls_loading: "Loading policies...",

    // ─── Studio: Policy Editor ───────────────────────────────────
    studio_policy_edit: "Edit Policy",
    studio_policy_create: "Create New Policy",
    studio_policy_defining_rules: "Defining access rules for",
    studio_policy_cancel: "Cancel",
    studio_policy_save: "Save Policy",
    studio_policy_template: "Start from a template (Optional)",
    studio_policy_select_template: "Select a template...",
    studio_policy_name: "Policy Name",
    studio_policy_name_placeholder: "Give your policy a descriptive name",
    studio_policy_behavior: "Policy Behavior",
    studio_policy_permissive: "Permissive",
    studio_policy_permissive_desc: "Combined using \"OR\"",
    studio_policy_restrictive: "Restrictive",
    studio_policy_restrictive_desc: "Combined using \"AND\"",
    studio_policy_command: "Policy Command",
    studio_policy_target_roles: "Target Roles",
    studio_policy_roles_placeholder: "Select roles (defaults to public)",
    studio_policy_using_expr: "USING expression",
    studio_policy_using_expr_desc: "Applies to existing rows (SELECT, UPDATE, DELETE)",
    studio_policy_check_expr: "WITH CHECK expression",
    studio_policy_check_expr_desc: "Applies to new or modified rows (INSERT, UPDATE)",
    studio_policy_help_title: "Row Level Security (RLS) Guide",
    studio_policy_help_intro: "Row Level Security (RLS) allows you to restrict which rows a user can access or modify based on their authentication status or roles.",
    studio_policy_help_step1_title: "1. Select a Template",
    studio_policy_help_step1_desc: "If you're new to RLS, the easiest way to start is by selecting a predefined template from the dropdown. It will automatically configure the policy for common use cases like \"Users can update their own rows\".",
    studio_policy_help_step2_title: "2. Target Roles",
    studio_policy_help_step2_desc: "Choose which users the policy applies to:",
    studio_policy_help_role_public: "Applies to everyone (logged in or not).",
    studio_policy_help_role_authenticated: "Only applies to users who are logged in.",
    studio_policy_help_role_anon: "Only applies to users who are NOT logged in.",
    studio_policy_help_step3_title: "3. USING Expression",
    studio_policy_help_step3_desc: "A SQL condition that must evaluate to true for existing rows to be visible or updatable (SELECT, UPDATE, DELETE).",
    studio_policy_help_step3_example: "Ensures users can only see rows where they are the owner.",
    studio_policy_help_step4_title: "4. WITH CHECK Expression",
    studio_policy_help_step4_desc: "A SQL condition that must evaluate to true for new or modified rows to be saved (INSERT, UPDATE).",
    studio_policy_help_step4_example: "Usually the same as the USING expression when users are only allowed to insert/edit their own data.",
    studio_policy_help_auth_vars_title: "Rebase Auth Variables",
    studio_policy_help_auth_vars_desc: "Rebase provides native helper functions that you can use in your expressions:",
    studio_policy_help_auth_uid: "Returns the current user's ID as text. Example:",
    studio_policy_help_auth_jwt: "Returns the full JWT payload as JSONB so you can check custom claims. Example:",
    studio_policy_help_auth_roles: "Returns the user's role IDs as a comma-separated string. Best used with:",
    studio_policy_help_docs_cta: "To learn more about Postgres policies, check out the official documentation.",
    studio_policy_help_read_docs: "Read Docs",
    studio_policy_help_got_it: "Got it!",

    // ─── Studio: UI Actions ──────────────────────────────────────
    studio_add_kanban_column: "Add column",
    studio_add_kanban_column_title: "Add Kanban Column",
    studio_add_kanban_column_name: "Column name",
    studio_add_kanban_column_name_placeholder: "New column name",
    studio_add_kanban_column_add: "Add",
    studio_add_kanban_column_cancel: "Cancel",
    studio_collection_view_sql: "SQL Editor",
    studio_collection_view_admin: "Admin View",
    studio_editor_collection_tooltip: "Edit collection schema",
    studio_editor_collection_no_permission: "You don't have permission to edit this collection",
    studio_editor_collection_start_tooltip: "Copy path or edit schema",
    studio_editor_collection_start_no_permission: "You don't have permission to modify this collection",
    studio_editor_collection_start_copied: "Copied!",
    studio_editor_entity_tooltip: "Edit entity schema",
    studio_editor_entity_no_permission: "You don't have permission to edit this entity",
    studio_home_edit_collection: "Edit collection",
    studio_home_delete_collection: "Delete collection",
    studio_home_confirm_delete_title: "Delete collection?",
    studio_home_confirm_delete_body: "Are you sure you want to delete the collection \"{{name}}\"? This action cannot be undone.",
    studio_kanban_setup: "Kanban Setup",
    studio_missing_reference: "Missing reference: {{path}}",
    studio_missing_reference_create: "Create",
    studio_new_collection: "New collection",
    studio_new_collection_card: "New collection",
    studio_new_collection_no_permission: "You don't have permissions to create collections",
    studio_property_add_column: "Add new property",
    studio_property_add_column_no_permission: "You don't have permission to add new properties",
    studio_add_kanban_column_desc: "Add a new option to the \"{{property}}\" property. This will create a new column in the Kanban board.",
    studio_add_kanban_column_adding: "Adding...",
    studio_collection_view_edit: "Edit",
    studio_editor_collection_disabled: "Collection editing is disabled",
    studio_editor_collection_edit: "Edit collection",
    studio_editor_entity_save_first: "You need to save the document before changing the schema",
    studio_editor_entity_edit_schema: "Edit schema for this form",
    studio_editor_collection_start_save_filter: "Save default filter and sort",
    studio_editor_collection_start_clear_filter: "Clear default filter and sort",
    studio_editor_collection_start_reset_filter: "Reset to default filter and sort",
    studio_editor_collection_start_saved: "Default config saved",
    studio_home_duplicate_collection: "Duplicate",
    studio_home_delete: "Delete",
    studio_home_confirm_delete_no_data: "This will not delete any data, only the collection in the admin panel",
    studio_home_collection_deleted: "Collection deleted",
    studio_kanban_configure: "Configure Kanban",
    studio_missing_reference_error: "No collection for path: {{path}}",
    studio_new_collection_add: "Add new collection",

    // ─── Property field labels ──────────────────────────────────
    db_column_type: "Database Column Type",
    primary_key_unique_id: "Primary Key / Unique ID",
    spread_children_as_columns: "Spread children as columns",
    mode: "Mode",
    timezone: "Timezone",
    target_collection: "Target collection",

    // ─── Storage property ───────────────────────────────────────
    storage_file_name: "File name",
    storage_path: "Storage path",
    storage_max_size: "Max size (in bytes)",
    storage_resize_mode: "Resize mode",
    storage_output_format: "Output format",
    storage_max_width: "Max width (px)",
    storage_max_height: "Max height (px)",
    storage_quality: "Quality (0-100)",
    storage_file_upload_config: "File upload config",
    storage_image_resize_config: "Image Resize Configuration",
    storage_all_file_types: "All file types allowed",
    storage_allowed_file_types: "Allowed file types",
    storage_include_bucket_url: "Include bucket URL (s3://...) in saved value",
    storage_save_url: "Save URL instead of storage path",

    // ─── DateTime property ──────────────────────────────────────
    datetime_automatic_value: "Automatic value",

    // ─── Markdown property ──────────────────────────────────────
    markdown_paste_behavior: "Paste behavior",

    // ─── Entity identity bar and inspector ──────────────────────
    entity_saved: "Saved",
    more_actions: "More actions",
    record_history: "History",
    inspect_record: "Inspect record",
    inspect: "Inspect",
    close_inspector: "Close inspector",
    open_in_live_site: "Open in the live site",

    // ─── Revision history ───────────────────────────────────────
    history_revert: "Revert to this version",
    history_revert_title: "Revert data to this version?",
    history_reverted: "Reverted to selected version",
    history_revert_error: "Error reverting entity",
    history_revert_dirty: "Please save or discard your changes before reverting",
    history_empty: "No history available",
    history_empty_description: "When you save a record, a new version is created and stored in the history.",
    history_new_entity: "History is only available for existing records",
    history_no_more: "No more history",

    // ─── Studio: tools, groups and empty states ─────────────────
    // The admin's own three groups, read by `useNavigationGroupLabel` the same
    // way as the Studio ones below — the group *name* is an identifier the
    // ordering and the icons key off, and only the heading is translated.
    studio_group_views: "Views",
    studio_group_admin: "Admin",
    studio_group_settings: "Settings",
    studio_group_database: "Database",
    studio_group_compute: "Compute",
    studio_group_storage: "Storage",
    studio_group_api: "API",
    studio_group_access_control: "Access Control",
    studio_tool_sql: "SQL Console",
    studio_tool_sql_description: "Execute SQL queries",
    studio_tool_js: "JS Console",
    studio_tool_js_description: "Execute JavaScript",
    studio_tool_rls: "RLS Policies",
    studio_tool_rls_description: "Row Level Security",
    studio_tool_storage: "Storage",
    studio_tool_storage_description: "Manage storage files",
    studio_tool_cron: "Cron Jobs",
    studio_tool_cron_description: "Manage scheduled tasks",
    studio_tool_schema_visualizer: "Schema Visualizer",
    studio_tool_schema_visualizer_description: "Interactive database ERD",
    studio_tool_branches: "Branches",
    studio_tool_branches_description: "Create and manage database branches",
    studio_tool_backups: "Backups",
    studio_tool_backups_description: "Download database backups",
    studio_tool_api: "API Explorer",
    studio_tool_api_description: "Interactive API documentation and testing",
    studio_tool_logs: "Logs Explorer",
    studio_tool_logs_description: "Real-time system and query logs",
    studio_tool_api_keys: "API Keys",
    studio_tool_api_keys_description: "Create and manage scoped API keys",
    studio_backups_denied_title: "You cannot list this project's backups",
    studio_backups_denied_hint: "Nothing is wrong with the project. Listing backups needs an admin connection, and the signed-in account was refused.",
    studio_backups_read_failed: "Could not read this project's backups",
    studio_backups_downloading: "Downloading…",
    studio_backups_roles_file: "Roles file",
    studio_backups_roles_file_hint: "The .globals.sql saved with this backup. It recreates the database roles that the dump's grants and row-level security refer to, and `rebase db restore` reads it from next to the .dump, so keep the two files together.",
    studio_backups_no_roles_file: "No roles file",
    studio_backups_no_roles_file_hint: "No .globals.sql was saved with this backup. Restoring it into a new Postgres stops at the first grant to a database role that does not exist there.",
    studio_cron_denied_title: "You cannot list this project's cron jobs",
    studio_cron_denied_hint: "Nothing is wrong with the project. The cron API is admin-only, and the signed-in account was refused.",
    studio_cron_read_failed: "Could not read this project's cron jobs",
    studio_api_keys_denied_title: "You cannot list this project's API keys",
    studio_api_keys_denied_hint: "Nothing is wrong with the project. Managing API keys is an admin operation, and the signed-in account was refused.",
    studio_api_keys_read_failed: "Could not read this project's API keys",
    studio_api_keys_empty_title: "No API keys yet",
    studio_api_keys_empty_hint: "Create a key to enable machine-to-machine authentication",
    studio_api_keys_revoke: "Revoke",
    studio_api_keys_select_hint: "Select an API key to view details",
    studio_api_keys_permissions: "Permissions",
    studio_api_keys_no_permissions: "No permissions configured",
    studio_api_keys_revoke_confirmation: "Revoke confirmation",
    studio_api_keys_name_label: "Name:",
    studio_api_keys_access_label: "Access:",
    studio_api_keys_admin_granted: "Admin role granted",
    studio_api_keys_admin_granted_hint: "the admin routes and the default_admin policies",
    studio_api_keys_done: "Done",
    studio_api_keys_status_active: "Active",
    studio_api_keys_status_expired: "Expired",
    studio_api_keys_status_revoked: "Revoked",
    studio_api_keys_stat_status: "Status",
    studio_api_keys_stat_last_used: "Last used",
    studio_api_keys_stat_expires: "Expires",
    studio_api_keys_never: "Never",
    studio_api_keys_role_service: "Service",
    studio_api_keys_stat_rate_limit: "Rate limit",
    studio_api_keys_rate_limit_default: "Default (1000/15min)",
    studio_api_keys_stat_created_by: "Created by",
    studio_api_keys_copy_failed: "Failed to copy",
    studio_branches_denied_title: "You cannot list this project's branches",
    studio_branches_denied_hint: "Nothing is wrong with the project. Branching runs on the admin connection, and the signed-in account was refused.",
    studio_branches_read_failed: "Could not read this project's branches",
    studio_schema_no_collections: "No collections declared",
    studio_schema_no_collections_body: "There is nothing to draw yet. Add a collection under config/collections/, or open Edit collections to add one here.",
    studio_schema_introspect_hint: "Against a database that already has tables, rebase schema introspect writes the collection files for you.",


    // ─── Studio: RLS editor confirmations ───────────────────────
    studio_rls_enable_confirm_title: "Enable Row Level Security?",
    studio_rls_enable_confirm_body: "Until a policy allows it, every row of {{table}} becomes unreadable. Postgres denies by default.",
    studio_rls_disable_confirm_title: "Disable Row Level Security?",
    studio_rls_disable_confirm_body: "Every policy on {{table}} stops being enforced. Anyone who can reach the table can read and write every row.",
    studio_rls_drop_policy_title: "Drop this policy?",
    studio_rls_drop_policy_body: "{{policy}} is removed from {{table}} in the database. Nothing is written to your code, so a deploy can restore it.",
    studio_rls_unapplied: "Unapplied",
    studio_rls_unapplied_tooltip: "Declared in your code, not yet in the database. Save it here to plan and apply the change, or run `rebase db push`.",


    // ─── Studio: empty states that name a remedy ────────────────
    studio_branches_unavailable_title: "Database branching is not available",
    studio_branches_unavailable_body: "Branching needs a Postgres backend with an admin connection. Set ADMIN_CONNECTION_STRING in the backend's environment.",
    studio_backups_not_configured_title: "Backups are not configured",
    studio_backups_not_configured_body: "Set BACKUP_DESTINATION to a local path or an s3:// or gs:// URL, then create a backup with `rebase db backup` or a scheduled cron.",
    studio_cron_empty_title: "No cron jobs registered",
    studio_cron_empty_body: "Add a file under backend/crons/ that default-exports a CronJobDefinition.",
    studio_read_the_docs: "Read the documentation"
};
